/**
 * Kwaliteit (F4) — types + Zod schemas.
 *
 * Bron: fs-pro src/pages/kwaliteit/KwaliteitPage.tsx (task list met
 * omschrijving, prioriteit, status, deadline, toegewezen aan).
 */

import { z } from 'zod'
import { queryBoolean } from './zod-query'
import { idParam } from './id-validators'

// ─── Enums (matchen Prisma-enums als string unions) ──────────────────────────

export const KWALITEIT_PRIORITEIT = ['HOOG', 'NORMAAL', 'LAAG'] as const
export type KwaliteitsTaakPrioriteit = (typeof KWALITEIT_PRIORITEIT)[number]

export const KWALITEIT_STATUS = ['OPEN', 'IN_BEHANDELING', 'GEREED'] as const
export type KwaliteitsTaakStatus = (typeof KWALITEIT_STATUS)[number]

// ─── UI labels ──────────────────────────────────────────────────────────────

export const KWALITEIT_PRIORITEIT_LABELS: Record<KwaliteitsTaakPrioriteit, string> = {
  HOOG:    'Hoog',
  NORMAAL: 'Normaal',
  LAAG:    'Laag',
}

export const KWALITEIT_STATUS_LABELS: Record<KwaliteitsTaakStatus, string> = {
  OPEN:           'Open',
  IN_BEHANDELING: 'In behandeling',
  GEREED:         'Gereed',
}

/**
 * Badge-varianten — overgenomen uit fs-pro `priorityColors` /
 * `statusColors`. We gebruiken de varianten uit `@streep/ui-fp Badge`.
 */
export const KWALITEIT_PRIORITEIT_BADGE_VARIANT: Record<
  KwaliteitsTaakPrioriteit,
  'danger' | 'warning' | 'info'
> = {
  HOOG:    'danger',
  NORMAAL: 'warning',
  LAAG:    'info',
}

export const KWALITEIT_STATUS_BADGE_VARIANT: Record<
  KwaliteitsTaakStatus,
  'warning' | 'info' | 'success'
> = {
  OPEN:           'warning',
  IN_BEHANDELING: 'info',
  GEREED:         'success',
}

// ─── DTO ────────────────────────────────────────────────────────────────────

export interface KwaliteitsTaakDTO {
  id: string
  omschrijving: string
  prioriteit: KwaliteitsTaakPrioriteit
  status: KwaliteitsTaakStatus
  /** ISO yyyy-MM-dd in API-payload, of null. */
  deadline: string | null
  /** Employee.id van de toegewezen monteur, of null. */
  toegewezenAan: string | null
  /** Voor display in lijst: voorkomt extra round-trip naar /medewerkers. */
  toegewezenAanNaam: string | null
  /** Optionele werkbon waar deze bevinding bij hoort. */
  werkbonId: string | null
  /** Werkbon-nummer voor display in portal/backoffice lijsten. Null als geen werkbon. */
  werkbonNummer: string | null
  /** F6: true als automatisch aangemaakt door steekproef-rule bij werkbon-goedkeuring. */
  autoGegenereerd: boolean
  /** F6: template-key (TOOLBOX / VEILIGHEID / KAM / STEEKPROEF) of null voor vrije taak. */
  templateKey: string | null
  /** F6: aantal gekoppelde Document-rijen (foto's, rapporten). */
  aantalDocumenten: number
  aangemaaktDoor: string | null
  laatstGewijzigdDoor: string | null
  createdAt: string
  updatedAt: string
}

/** KPI-tegels boven de lijst (aantal per status + open met deadline verstreken). */
export interface KwaliteitsStatsDTO {
  totaal: number
  open: number
  inBehandeling: number
  gereed: number
  /** Open of in-behandeling met deadline < vandaag. */
  achterstallig: number
  /** Aantal HOOG-prioriteit taken met status != GEREED. */
  hoogActief: number
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet ISO yyyy-MM-dd zijn')

export const createKwaliteitsTaakSchema = z.object({
  omschrijving: z.string().trim().min(1, 'Omschrijving is verplicht').max(500),
  prioriteit:   z.enum(KWALITEIT_PRIORITEIT).default('NORMAAL'),
  status:       z.enum(KWALITEIT_STATUS).default('OPEN'),
  deadline:     isoDateSchema.nullable().optional(),
  toegewezenAan: idParam().nullable().optional(),
  werkbonId:    idParam().nullable().optional(),
  /** F6: template-key (TOOLBOX / VEILIGHEID / KAM / STEEKPROEF) of null. */
  templateKey:  z.string().trim().min(1).max(40).nullable().optional(),
})

export type CreateKwaliteitsTaakInput = z.infer<typeof createKwaliteitsTaakSchema>

export const updateKwaliteitsTaakSchema = z.object({
  omschrijving: z.string().trim().min(1).max(500).optional(),
  prioriteit:   z.enum(KWALITEIT_PRIORITEIT).optional(),
  status:       z.enum(KWALITEIT_STATUS).optional(),
  deadline:     isoDateSchema.nullable().optional(),
  toegewezenAan: idParam().nullable().optional(),
  werkbonId:    idParam().nullable().optional(),
  templateKey:  z.string().trim().min(1).max(40).nullable().optional(),
})

export type UpdateKwaliteitsTaakInput = z.infer<typeof updateKwaliteitsTaakSchema>

export const listKwaliteitsTakenQuerySchema = z.object({
  status:        z.enum(KWALITEIT_STATUS).optional(),
  prioriteit:    z.enum(KWALITEIT_PRIORITEIT).optional(),
  toegewezenAan: idParam().optional(),
  /** F6: filter op werkbon (voor "kwaliteit op werkbon X"-blok). */
  werkbonId:     idParam().optional(),
  /** F6: portal-specifiek — "alleen mijn taken". Geforceerd in API voor portal-users. */
  alleenEigen:   queryBoolean().optional(),
  /** F6: filter open vs. afgerond — "open" = OPEN + IN_BEHANDELING, "afgerond" = GEREED. */
  openVsGereed:  z.enum(['open', 'gereed']).optional(),
  search:        z.string().trim().max(100).optional(),
  page:          z.coerce.number().int().positive().default(1),
  pageSize:      z.coerce.number().int().positive().max(2000).default(50),
})

export type ListKwaliteitsTakenQuery = z.infer<typeof listKwaliteitsTakenQuerySchema>
